/**
 * Minimal replacement for jQuery Slick 1.9 (the theme used it for news, reviews and
 * logo carousels). Builds the same DOM (.slick-list > .slick-track > .slick-slide,
 * dots list) so the theme's slickSlider.min.css keeps applying.
 */
export type SlickOptions = {
  slidesToShow?: number
  slidesToScroll?: number
  infinite?: boolean
  dots?: boolean
  dotsClass?: string
  autoplay?: boolean
  autoplaySpeed?: number
  speed?: number
  pauseOnHover?: boolean
  responsive?: { breakpoint: number; settings: Partial<SlickOptions> }[]
}

const DEFAULTS: Required<Omit<SlickOptions, 'responsive'>> = {
  slidesToShow: 1,
  slidesToScroll: 1,
  infinite: true,
  dots: false,
  dotsClass: 'slick-dots',
  autoplay: false,
  autoplaySpeed: 3000,
  speed: 500,
  pauseOnHover: true,
}

export function slick(root: HTMLElement, options: SlickOptions) {
  if (root.classList.contains('slick-initialized')) return
  const originals = Array.from(root.children) as HTMLElement[]
  if (!originals.length) return

  let opts = { ...DEFAULTS, ...options }
  let current = 0
  let timer: number | undefined
  let slideWidth = 0
  let cloneCount = 0

  const list = document.createElement('div')
  list.className = 'slick-list draggable'
  const track = document.createElement('div')
  track.className = 'slick-track'
  list.appendChild(track)
  root.appendChild(list)
  root.classList.add('slick-initialized', 'slick-slider')
  let dots: HTMLUListElement | null = null

  const active = () => {
    const width = window.innerWidth
    const bp = (options.responsive ?? []).filter((r) => width < r.breakpoint).sort((a, b) => a.breakpoint - b.breakpoint)[0]
    return { ...DEFAULTS, ...options, ...(bp?.settings ?? {}) }
  }

  const count = originals.length
  const pages = () => (opts.infinite ? Math.ceil(count / opts.slidesToScroll) : Math.ceil((count - opts.slidesToShow) / opts.slidesToScroll) + 1)
  const sliding = () => count > opts.slidesToShow

  const build = () => {
    opts = active()
    track.innerHTML = ''
    cloneCount = sliding() && opts.infinite ? opts.slidesToShow : 0
    const slides: HTMLElement[] = []
    const cloneOf = (el: HTMLElement, index: number) => {
      const c = el.cloneNode(true) as HTMLElement
      c.classList.add('slick-slide', 'slick-cloned')
      c.dataset.slickIndex = String(index)
      c.setAttribute('aria-hidden', 'true')
      c.tabIndex = -1
      return c
    }
    for (let i = count - cloneCount; i < count; i++) slides.push(cloneOf(originals[i], i - count))
    originals.forEach((el, i) => {
      el.classList.add('slick-slide')
      el.dataset.slickIndex = String(i)
      slides.push(el)
    })
    for (let i = 0; i < cloneCount; i++) slides.push(cloneOf(originals[i], count + i))
    slides.forEach((s) => track.appendChild(s))

    dots?.remove()
    dots = null
    root.classList.toggle('slick-dotted', opts.dots && sliding())
    if (opts.dots && sliding()) {
      dots = document.createElement('ul')
      dots.className = opts.dotsClass
      dots.setAttribute('role', 'tablist')
      for (let p = 0; p < pages(); p++) {
        const li = document.createElement('li')
        li.setAttribute('role', 'presentation')
        const b = document.createElement('button')
        b.type = 'button'
        b.setAttribute('role', 'tab')
        b.textContent = String(p + 1)
        b.addEventListener('click', () => go(p * opts.slidesToScroll))
        li.appendChild(b)
        dots.appendChild(li)
      }
      root.appendChild(dots)
    }
    measure()
    go(Math.min(current, count - 1), false)
    restart()
  }

  const measure = () => {
    slideWidth = Math.ceil(list.clientWidth / opts.slidesToShow)
    const all = Array.from(track.children) as HTMLElement[]
    track.style.width = `${Math.ceil(slideWidth * all.length)}px`
    track.style.opacity = '1'
    const first = all[0]
    const cs = first ? getComputedStyle(first) : null
    const margins = cs ? parseFloat(cs.marginLeft) + parseFloat(cs.marginRight) : 0
    all.forEach((s) => (s.style.width = `${slideWidth - margins}px`))
  }

  const position = (index: number, animate: boolean) => {
    track.style.transition = animate ? `transform ${opts.speed}ms ease` : 'none'
    track.style.transform = `translate3d(${-(index + cloneCount) * slideWidth}px, 0px, 0px)`
  }

  const mark = () => {
    const all = Array.from(track.children) as HTMLElement[]
    all.forEach((s, i) => {
      const logical = i - cloneCount
      const visible = logical >= current && logical < current + opts.slidesToShow
      s.classList.toggle('slick-active', visible)
      s.classList.toggle('slick-current', logical === current)
      s.setAttribute('aria-hidden', visible ? 'false' : 'true')
    })
    if (dots) {
      const page = Math.floor(current / opts.slidesToScroll)
      Array.from(dots.children).forEach((li, i) => li.classList.toggle('slick-active', i === page))
    }
  }

  const go = (index: number, animate = true) => {
    if (!sliding()) {
      current = 0
      position(0, false)
      return mark()
    }
    const max = opts.infinite ? count : count - opts.slidesToShow
    let target = index
    if (!opts.infinite) target = Math.max(0, Math.min(index, max))
    position(target, animate)
    if (opts.infinite && (target >= count || target < 0)) {
      const wrapped = ((target % count) + count) % count
      window.setTimeout(() => position(wrapped, false), animate ? opts.speed : 0)
      target = wrapped
    }
    current = target
    mark()
  }

  const next = () => go(current + opts.slidesToScroll)
  const prev = () => go(current - opts.slidesToScroll)

  const restart = () => {
    window.clearInterval(timer)
    if (opts.autoplay && sliding()) timer = window.setInterval(next, opts.autoplaySpeed)
  }
  if (opts.pauseOnHover) {
    root.addEventListener('mouseenter', () => window.clearInterval(timer))
    root.addEventListener('mouseleave', restart)
  }

  // Swipe / drag
  let startX: number | null = null
  list.addEventListener('pointerdown', (e) => {
    startX = e.clientX
  })
  list.addEventListener('pointerup', (e) => {
    if (startX == null) return
    const dx = e.clientX - startX
    startX = null
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)()
  })

  let width = window.innerWidth
  window.addEventListener('resize', () => {
    if (window.innerWidth === width) return
    width = window.innerWidth
    build()
  })
  build()
}
